/**
 * <ticket-card> — the one place that knows what a ticket SAYS. Every surface
 * that shows a buyer's ticket mounts this tag: the buyer's own order page
 * (order-access.view.ts), the ops console's order sheet
 * (events/console/order-detail.view.ts), and the studio's proof of both
 * (the design studio). The card's DESIGN lives one package over — the two
 * shipped compositions and the scale frame are `@momo/ticket-template`'s
 * (`renderTicketCardScaled`), untouched by this file. What lives here is the
 * mapping onto that design, which had been written out three times.
 *
 * WHY THIS PACKAGE AND NOT `@momo/ticket-template`. That package's own
 * contract (`ticket.props.ts`) says a variant file never reaches for a
 * deployment value — props arrive already resolved, "sourced by the caller
 * from the WORKSPACE docs at render time". This element IS that caller: it
 * reads `brandName` and the app's own localization engine, both @momo/views
 * state. Putting it beside the templates would invert that boundary and drag
 * schmancy state into a package whose `/file` and `/email` subpaths render
 * under Node.
 *
 * WHY IT IS TOLD ITS TICKET RATHER THAN RESOLVING ONE. The line is whether
 * the view carries a state-backed subject of its own: a view that can ask
 * "which order am I" of a
 * singleton mounts bare; "a leaf handed an already-resolved fact to render
 * keeps that fact as a property". This card is that leaf, and not by taste —
 * there is no one singleton it could ask. Its three hosts resolve WHICH ticket
 * from three different places (the buyer's `orderAccessState`, the console's
 * `consoleTickets`, the studio's own fixture `@property`), each already inside
 * a `repeat()` that has picked the ticket by the time the card renders. A card
 * that re-resolved its own ticket by id would have to know which of the three
 * trees it was mounted under — the one thing that genuinely differs.
 *
 * WHAT IT IS TOLD IS THEREFORE ONLY WHAT ITS HOSTS GENUINELY HOLD DIFFERENTLY:
 * the ticket, the event, the venue. The buyer's page reads a token-scoped
 * `OrderAccess` projection frozen at fulfillment (no buyer name or email, by
 * design — a bare-capability link); the console reads the live `Order`/`Event`/
 * `Location` docs; the studio reads fixtures. Those three cannot be collapsed,
 * so they stay properties.
 *
 * WHAT IT WORKS OUT FOR ITSELF is everything that was only ever restated:
 *
 *   displayId — `Ticket.ticketDisplayId(ticketId)`, never passed in. The
 *     printed reference is a short stable digest of the ticket's own doc id,
 *     never the doc id itself: that id encodes the ORDER id, so printing it
 *     leaks one buyer's order key onto a ticket they may hand to a friend
 *     (ticket.entity.ts). It is derived here so the reference on the buyer's
 *     screen, on the operator's preview and on the emailed file are the same
 *     string — an operator cross-referencing a buyer's screenshot is reading
 *     the same ticket. This was the one that had already drifted into a
 *     hand-copied hash.
 *
 *   brand — the live `brandName` singleton. Workspace identity is the same
 *     fact for every card on the page, and each app package already mounts its
 *     own reconciler for it (web's workspace.sync.ts from Firestore,
 *     the design studio's from FIXTURE_WORKSPACE), so reading the singleton is
 *     what makes the studio's card honest rather than what makes it fake.
 *
 *   brand.logoUrl — the live `brandLogo` singleton, off the same workspace
 *     card `brandName` reads, and already fetchable as stored (the settings
 *     picker writes a download URL, a seam-provisioned workspace a data URI),
 *     so it is handed over as-is. The Stencil variant draws it in place of
 *     its generated mirrored brand wordmark; every other variant ignores it.
 *     It is the operator's own stored mark — the storefront asset — and
 *     deliberately not `walletPassMarkUri`'s whitened variant: the card
 *     paints the mark as a silhouette in its own ink, so it needs no
 *     polarity chosen for it, exactly as `<brand-logo>` needs none.
 *
 *   brand's own colors — null, deliberately and here only: no shipped card
 *     variant reads an accent color (event.entity.ts's `templateVariant`
 *     picks the composition). A caller passing one would be handing over a
 *     value nothing renders. When a variant grows one, this is the single
 *     line that changes.
 *
 *   event.posterUrl — hardcoded null for the same reason: no shipped card
 *     variant draws the event poster. event.logoUrl is NOT hardcoded here —
 *     the Stencil variant draws the event's own uploaded wordmark
 *     (`Event.logo`, ticket.props.ts's `TicketTemplateEvent.logoUrl`) when
 *     present, falling back to its generated mirrored wordmark when absent —
 *     so `TicketCardEvent` (below) carries it straight through from whatever
 *     a host resolves and passes.
 *
 *   locale — defaults to the surface's own UI language. The rule is that a
 *     ticket reads in its HOLDER's language: on the buyer's page the viewer is
 *     the holder, so the UI language is theirs by construction; a surface
 *     showing someone else's ticket (the console, the studio's proof of it)
 *     knows the holder's own `Order.buyerLocale` and passes it. `OrderAccess`
 *     carries no locale field at all, which is why this is a property with a
 *     default rather than something the card could resolve outright.
 *
 * `capture` mounts the scale frame's second, off-screen, untransformed 384px
 * twin as the rasterize target for a share action — see
 * `ticket-inapp.scaled-card.ts`'s own header for why a capture must never be
 * taken from the visible scaled node. It is opt-in because a surface with no
 * share action would otherwise render every card twice for nothing. The host
 * reaches it through `captureNode()`: the twin lives in this element's shadow
 * root, so a host `querySelector` can no longer find it directly.
 */
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import type { Locale } from "@momo/i18n";
import {
	renderTicketCardScaled,
	type TicketTemplateEvent,
	type TicketTemplateTicket,
} from "@momo/ticket-template";
import { Ticket, type TicketTemplateVariant } from "@momo/types";
import { css, html } from "lit";
import { property } from "lit/decorators.js";
import { LocalizeMixin } from "../i18n/localize.ts";
import { brandLogo, brandName } from "../workspace/workspace.state.ts";

/** The ticket facts a host holds and this card cannot derive. `displayId` is
 *  omitted on purpose — it is the digest this card computes from `ticketId`
 *  (see the header); `templateVariant` is the composition frozen onto the
 *  ticket at fulfillment, never re-resolved at render. */
export type TicketCardTicket = Omit<TicketTemplateTicket, "displayId"> & {
	readonly ticketId: string;
	readonly templateVariant: TicketTemplateVariant;
};

/** The event/venue context, minus the poster — no shipped variant draws one,
 *  so there is nothing for a host to resolve or pass. `logoUrl` stays: the
 *  Stencil variant draws it when a host resolves and supplies one (see the
 *  class header above). */
export type TicketCardEvent = Omit<TicketTemplateEvent, "posterUrl">;

@schmancyElement("ticket-card")
export class TicketCard extends LocalizeMixin(SchmancyElement) {
	// The scale frame emits a fixed-pixel box, so the host element hugs it
	// rather than stretching to whatever column it was dropped into.
	static styles = [
		css`
			:host {
				display: block;
				width: max-content;
			}
		`,
	];

	@property({ attribute: false }) ticket!: TicketCardTicket;

	@property({ attribute: false }) event!: TicketCardEvent;

	/** 1-based position and the order's total ticket count — rendered as
	 *  "2 / 3". Null for a single-ticket order, and null while an order is
	 *  still minting: a count that the next render revises is a lie. */
	@property({ attribute: false }) sequence: {
		readonly index: number;
		readonly count: number;
	} | null = null;

	/** The card's on-screen width. A pixel number, not a fluid CSS width: the
	 *  composition is laid out at its native 384px and transform-scaled whole,
	 *  because a ticket must scale, never re-flow. */
	@property({ type: Number }) width = 384;

	/** The holder's own language — named for whose language it is, not merely
	 *  "locale", because `LocalizeMixin` already owns that word for the
	 *  SURFACE's language and the two are the same only where the viewer is
	 *  the holder. Null falls back to exactly that case. */
	@property({ attribute: false }) holderLocale: Locale | null = null;

	/** Whether to also mount the off-screen 384px twin `captureNode()` returns.
	 *  Set it only on a surface that actually shares. */
	@property({ type: Boolean }) capture = false;

	/** The rasterize target for a share action — the untransformed twin, never
	 *  the visible scaled node. Null when `capture` is false or before the
	 *  first render. */
	captureNode(): HTMLElement | null {
		return this.renderRoot.querySelector<HTMLElement>("[data-ticket-canvas]");
	}

	override render() {
		const { ticket, event } = this;
		return html`
			${renderTicketCardScaled(
				ticket.templateVariant,
				{
					locale: this.holderLocale ?? this.uiLanguage,
					brand: {
						name: brandName.get(),
						logoUrl: brandLogo.get(),
						colors: null,
					},
					event: { ...event, posterUrl: null },
					ticket: {
						displayId: Ticket.ticketDisplayId(ticket.ticketId),
						typeName: ticket.typeName,
						qrToken: ticket.qrToken,
					},
					sequence: this.sequence,
				},
				// The capture id IS the ticket id — the same string both share
				// hosts already query by, so the hook can't drift from the card.
				this.capture
					? { width: this.width, captureId: ticket.ticketId }
					: { width: this.width },
			)}
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"ticket-card": TicketCard;
	}
}
